import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { AddMarkStep, RemoveMarkStep } from '@tiptap/pm/transform';

/** Document ranges used by the spell checker. */
export interface Range { from: number; to: number }

export function clampRange(doc: ProseMirrorNode, range: Range): Range {
  const size = doc.content.size;
  const from = Math.max(0, Math.min(range.from - 1, size));
  return { from, to: Math.max(from, Math.min(range.to + 1, size)) };
}

export function wholeDocument(doc: ProseMirrorNode): Range { return { from: 0, to: doc.content.size }; }

/** Text blocks touched by the ranges, keyed by position. */
export function textblocksIn(doc: ProseMirrorNode, ranges: readonly Range[]): Map<number, ProseMirrorNode> {
  const blocks = new Map<number, ProseMirrorNode>();
  for (const range of ranges) {
    const { from, to } = clampRange(doc, range);
    if (to <= from) continue;
    doc.nodesBetween(from, to, (node, pos) => {
      if (node.isTextblock) { blocks.set(pos, node); return false; }
      return true;
    });
  }
  return blocks;
}

/** New-document ranges changed by a transaction, including mark-only changes (links, code). */
export function changedRanges(tr: Transaction): Range[] {
  let ranges: Range[] = [];
  tr.steps.forEach((step, index) => {
    const map = tr.mapping.maps[index]!;
    ranges = ranges.map(range => ({ from: map.map(range.from, -1), to: map.map(range.to, 1) }));
    map.forEach((_oldStart, _oldEnd, start, end) => ranges.push({ from: start, to: end }));
    if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) ranges.push({ from: step.from, to: step.to });
  });
  return ranges;
}
